import { Box, Button, Stack, Tooltip, Typography } from "@mui/material";
import { Flex, HStack } from "@zfp-akademie-a-s/zfp-design";
import { CheckCircleIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import { InProgressIcon } from "Components/_Shared/Icons/Icons";
import { type AnalysisSharedOutputs, ShannonPdfInfoStatus } from "Infrastructure/ApiZfp/Schema";
import { useFormatLocale } from "Infrastructure/Hooks/DateFns/useFormatLocale";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { ClientOutputType } from "./types";

interface ClientOutputItemRowProps {
  buttonText: string;
  onClick: () => void;
  disabled?: boolean;
  tooltipTitle?: string | null;
  type: ClientOutputType;
  data: AnalysisSharedOutputs;
}

export const ClientOutputItem = ({ buttonText, onClick, type, disabled = false, tooltipTitle = null, data }: ClientOutputItemRowProps) => {
  const { t } = useResource();
  const formatDate = useFormatLocale();

  let statusText = "";
  let icon = null;
  let iconColor = "inherit";

  switch (type) {
    case ClientOutputType.Email: {
      const sentAt = data.emailedPdf?.sentAt;
      const createdAt = data.emailedPdf?.createdAt;
      if (sentAt) {
        statusText = t(Resources.AnalysisDetail.ClientResult.PdfResult.Email.EmailSentAt, {
          date: formatDate(new Date(sentAt), "dd.M.yyyy"),
        });
        icon = <CheckCircleIcon />;
        iconColor = "success.main";
        break;
      }
      if (createdAt) {
        statusText = t(Resources.AnalysisDetail.ClientResult.PdfResult.Email.EmailSending);
        icon = <InProgressIcon sx={{ fontSize: 18 }} />;
        iconColor = "info.main";
      }
      break;
    }
    case ClientOutputType.MobileApp: {
      const isLatest = data.shannonPdf?.isLatest;
      if (!isLatest) break;

      const status = data.shannonPdf?.status;
      const updatedAt = data.shannonPdf?.updatedAt;
      if (status === ShannonPdfInfoStatus.finished && updatedAt) {
        statusText = t(Resources.AnalysisDetail.ClientResult.PdfResult.MobileApp.EnabledAt, {
          date: formatDate(new Date(updatedAt), "dd.M.yyyy"),
        });
        icon = <CheckCircleIcon />;
        iconColor = "success.main";
        break;
      }
      if (status === ShannonPdfInfoStatus.queued || status === ShannonPdfInfoStatus.in_progress) {
        statusText = t(Resources.AnalysisDetail.ClientResult.PdfResult.MobileApp.Processing);
        icon = <InProgressIcon sx={{ fontSize: 18 }} />;
        iconColor = "info.main";
      }
      break;
    }
  }

  const button = (
    <Button
      variant="contained"
      color="secondary"
      disabled={disabled}
      onClick={onClick}
      sx={{
        "&.Mui-disabled": {
          cursor: "not-allowed",
          pointerEvents: "auto",
        },
      }}
    >
      {buttonText}
    </Button>
  );

  return (
    <HStack>
      <Flex sx={{ width: "300px" }}>
        {tooltipTitle ? (
          <Tooltip title={tooltipTitle}>
            <Box>{button}</Box>
          </Tooltip>
        ) : (
          button
        )}
      </Flex>

      <Flex>
        <Stack direction="row" spacing={Spacing.M} alignItems="center">
          <Typography color={iconColor} display="flex">
            {icon}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {statusText}
          </Typography>
        </Stack>
      </Flex>
    </HStack>
  );
};
